"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface NumberInputProps
  extends Omit<
    React.ComponentProps<"input">,
    "size" | "type" | "value" | "onChange"
  > {
  /** Label text displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** When true, shows "(optional)" after the label. Required is the default. */
  optional?: boolean;
  /** When true, displays error styling */
  isError?: boolean;
  /** Error message that replaces helperText when isError is true */
  errorMessage?: string;
  /** Size variant of the input */
  size?: "sm" | "md" | "lg";
  /** Additional class name for the wrapper element */
  wrapperClassName?: string;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step increment (default: 1) */
  step?: number;
  /** Current value (controlled) */
  value?: number | "";
  /** Change handler */
  onChange?: (value: number | undefined) => void;
  /** Hide stepper buttons */
  hideControls?: boolean;
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className,
      label,
      helperText,
      optional = false,
      isError = false,
      errorMessage,
      size = "md",
      disabled,
      id,
      wrapperClassName,
      min,
      max,
      step = 1,
      value,
      onChange,
      hideControls = false,
      onKeyDown,
      onBlur,
      ...props
    },
    ref
  ) => {
    // Internal ref for when external ref is not provided
    const internalRef = React.useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    // Generate a unique ID for accessibility if not provided
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const helperId = `${inputId}-helper`;
    const hasHelperOrError = !!(helperText || (isError && errorMessage));

    // Size-specific classes
    const sizeClasses = {
      sm: {
        input: "py-(--input-sm-padding-y) body-xs-regular",
        paddingL: "pl-(--input-sm-padding-x)",
        // Extra padding on right for stepper buttons
        paddingR: "pr-(--input-sm-padding-x)",
        paddingRightWithControls: "pr-[calc(var(--input-sm-padding-x)+40px)]",
        icon: "size-[var(--input-sm-icon-size)]",
        buttonSize: "w-5 h-5",
        buttonIconSize: "text-[10px]",
      },
      md: {
        input: "py-(--input-md-padding-y) body-sm-regular",
        paddingL: "pl-(--input-md-padding-x)",
        paddingR: "pr-(--input-md-padding-x)",
        paddingRightWithControls: "pr-[calc(var(--input-md-padding-x)+48px)]",
        icon: "size-[var(--input-md-icon-size)]",
        buttonSize: "w-6 h-6",
        buttonIconSize: "text-xs",
      },
      lg: {
        input: "py-(--input-lg-padding-y) body-md-regular",
        paddingL: "pl-(--input-lg-padding-x)",
        paddingR: "pr-(--input-lg-padding-x)",
        paddingRightWithControls: "pr-[calc(var(--input-lg-padding-x)+56px)]",
        icon: "size-[var(--input-lg-icon-size)]",
        buttonSize: "w-7 h-7",
        buttonIconSize: "text-sm",
      },
    };

    const currentSize = sizeClasses[size];

    // Determine right padding based on controls visibility
    const paddingClasses = cn(
      currentSize.paddingL,
      hideControls ? currentSize.paddingR : currentSize.paddingRightWithControls
    );

    // Get current numeric value
    const getCurrentValue = (): number | undefined => {
      if (value === "" || value === undefined) return undefined;
      return typeof value === "number" ? value : undefined;
    };

    // Raw text while typing, so intermediate states like "-" or "5," survive the controlled
    // round-trip (they don't parse to a number, so `value` alone can't represent them).
    const [draft, setDraft] = React.useState<string | null>(null);

    const parseDraft = (text: string): number | undefined => {
      const numValue = parseFloat(text.replace(",", "."));
      return isNaN(numValue) ? undefined : numValue;
    };

    // Drop the draft once `value` changes to something it doesn't represent (stepper, clamping,
    // form reset), so a stale draft can't resurface if `value` later matches it again.
    const [prevValue, setPrevValue] = React.useState(value);
    if (value !== prevValue) {
      setPrevValue(value);
      if (draft !== null && parseDraft(draft) !== getCurrentValue()) {
        setDraft(null);
      }
    }

    // Show the draft only while it still agrees with `value`; any external change (stepper,
    // clamping, form reset) wins.
    const displayValue =
      draft !== null && parseDraft(draft) === getCurrentValue() ? draft : value;

    // Handle increment
    const handleIncrement = () => {
      if (disabled) return;

      const currentValue = getCurrentValue() ?? min ?? 0;
      let newValue = currentValue + step;

      if (max !== undefined && newValue > max) {
        newValue = max;
      }

      onChange?.(newValue);
    };

    // Handle decrement
    const handleDecrement = () => {
      if (disabled) return;

      const currentValue = getCurrentValue() ?? min ?? 0;
      let newValue = currentValue - step;

      if (min !== undefined && newValue < min) {
        newValue = min;
      }

      onChange?.(newValue);
    };

    // Handle direct input change -- accept comma as decimal separator.
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\./g, ',');

      // Only allow digits, minus, and comma decimal separator
      if (!/^-?\d*,?\d*$/.test(raw)) return;

      setDraft(raw);

      const numValue = parseDraft(raw);
      if (numValue === undefined) {
        onChange?.(undefined);
        return;
      }

      let finalValue = numValue;
      if (min !== undefined && finalValue < min) finalValue = min;
      if (max !== undefined && finalValue > max) finalValue = max;
      onChange?.(finalValue);
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setDraft(null);
      onBlur?.(e);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ".") {
        // Enforce comma as decimal separator.
        e.preventDefault();
      }
      onKeyDown?.(e);
    };

    // Check if at min/max bounds
    const isAtMin = min !== undefined && (getCurrentValue() ?? 0) <= min;
    const isAtMax = max !== undefined && (getCurrentValue() ?? 0) >= max;

    return (
      <div className={cn("flex flex-col gap-1", wrapperClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "body-sm-medium text-(--input-label)",
              disabled && "opacity-50"
            )}
          >
            {label}
            {optional && (
              <span className="text-(--input-label-optional) ml-1">
                (optional)
              </span>
            )}
          </label>
        )}

        {/* Input container */}
        <div className="relative">
          {/* Input element */}
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            inputMode="decimal"
            disabled={disabled}
            aria-invalid={isError}
            aria-describedby={hasHelperOrError ? helperId : undefined}
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            className={cn(
              // Base styles
              "w-full rounded-(--input-radius) border transition-colors",
              "shadow-(--input-shadow)",
              // Outline setup for focus (doesn't affect layout)
              "outline-1 -outline-offset-1 outline-transparent",
              currentSize.input,
              paddingClasses,

              // Default state
              "bg-(--input-bg) border-(--input-border) text-(--input-text)",

              // Placeholder
              isError
                ? "placeholder:text-(--input-text-placeholder-error)"
                : "placeholder:text-(--input-text-placeholder)",

              // Hover state (not disabled, not error)
              !isError && !disabled && "hover:border-(--input-border-hover)",

              // Focus state - uses outline to avoid layout shift
              !isError &&
                "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-focus)",

              // Error state
              isError && "border-(--input-border-error)",
              isError &&
                "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-error)",

              // Disabled state
              disabled && [
                "bg-(--input-bg-disabled)",
                "text-(--input-text-disabled)",
                "cursor-not-allowed",
                "pointer-events-none",
              ],

              // Hide native number spinners
              "[appearance:textfield]",
              "[&::-webkit-outer-spin-button]:appearance-none",
              "[&::-webkit-inner-spin-button]:appearance-none",

              className
            )}
            {...props}
          />

          {/* Stepper controls */}
          {!hideControls && (
            <div
              className={cn(
                "absolute top-1/2 -translate-y-1/2 right-(--input-md-padding-x) flex items-center gap-1",
                disabled && "opacity-50"
              )}
            >
              {/* Decrement button */}
              <button
                type="button"
                onClick={handleDecrement}
                disabled={disabled || isAtMin}
                aria-label="Decrease value"
                className={cn(
                  "flex items-center justify-center rounded-sm transition-colors",
                  currentSize.buttonSize,
                  "text-(--input-icon) hover:bg-bg-hover active:bg-bg-active",
                  (disabled || isAtMin) &&
                    "opacity-40 cursor-not-allowed hover:bg-transparent"
                )}
              >
                <i className={cn("pi pi-minus", currentSize.buttonIconSize)} />
              </button>

              {/* Increment button */}
              <button
                type="button"
                onClick={handleIncrement}
                disabled={disabled || isAtMax}
                aria-label="Increase value"
                className={cn(
                  "flex items-center justify-center rounded-sm transition-colors",
                  currentSize.buttonSize,
                  "text-(--input-icon) hover:bg-bg-hover active:bg-bg-active",
                  (disabled || isAtMax) &&
                    "opacity-40 cursor-not-allowed hover:bg-transparent"
                )}
              >
                <i className={cn("pi pi-plus", currentSize.buttonIconSize)} />
              </button>
            </div>
          )}
        </div>

        {/* Helper text / Error message */}
        {hasHelperOrError && (
          <p
            id={helperId}
            className={cn(
              "body-xs-regular",
              isError ? "text-(--input-helper-error)" : "text-(--input-helper)",
              disabled && "opacity-50"
            )}
          >
            {isError && errorMessage ? errorMessage : helperText}
          </p>
        )}
      </div>
    );
  }
);

NumberInput.displayName = "NumberInput";

export { NumberInput };

